import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { Homepage } from '@/features/dashboard/ui/Homepage';
import type {
  AudienceSparksDeps,
  OpportunitiesPreviewDeps,
} from '@/features/dashboard/ui/Homepage';
import { useLoadingIntro, startChatSession, useChatStore } from '@/features/chats';
import { getAgents } from '@/features/agents';
import { SPECIAL_AGENT_IDS } from '@/entities/agent';
import { OpportunitiesCard, getOpportunityById } from '@/entities/opportunity';
import { IQAnalysisPreview } from '@/features/analysis';
import { usePersonaSparks } from '@/features/persona-sparks';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { usePermissions, getCurrentOrganizationId } from '@/entities/user';
import { ROUTES } from '@/shared/config/routes';
import { useLastConnectedAt } from '@/features/opportunities-room';
import { useSpringboardTabs } from '@/features/springboard';
import type { OpportunityCardItem } from '@/entities/opportunity';
import type { ParsedSpark } from '@/entities/persona-spark';

/**
 * Bound `useSparks` hook for the AudienceSparks dashboard section.
 * Resolves workspace + permission gating here at the page boundary so
 * `features/dashboard` stays decoupled from `features/persona-sparks`,
 * `features/agents`, and `entities/{user,workspace}`.
 */
const useAudienceSparksData = () => {
  const workspaceId = useCurrentWorkspaceId();
  const { canViewAudiences, canOpenTool } = usePermissions();
  const canAccess = canViewAudiences && canOpenTool(SPECIAL_AGENT_IDS.AUDIENCE_FLOW);

  return usePersonaSparks({
    workspaceId,
    enabled: !!workspaceId && canAccess,
    getAgents,
  });
};

/**
 * Page entry for the home dashboard. Cross-feature dependencies are wired here
 * at the page boundary so `features/dashboard` stays decoupled from the features
 * it renders.
 */
export const HomePageDashboard = () => {
  const { t } = useTranslation('common');
  const { userName } = useLoadingIntro();
  const { tabs: springboardTabs } = useSpringboardTabs();
  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId();

  const lastVisitedAt = useLastConnectedAt();

  const openOpportunityItem = useCallback(
    (item: OpportunityCardItem) =>
      void navigate(ROUTES.TOOLS_OPPORTUNITIES, { state: { selectedOpportunity: item } }),
    [navigate],
  );

  const openOpportunitiesFallback = useCallback(
    () => void navigate(ROUTES.TOOLS_OPPORTUNITIES),
    [navigate],
  );

  const handleViewOpportunity = useCallback(
    async (opportunityId?: string) => {
      if (!opportunityId || !workspaceId) {
        openOpportunitiesFallback();
        return;
      }
      try {
        const item = await getOpportunityById(workspaceId, opportunityId);
        openOpportunityItem(item);
      } catch {
        openOpportunitiesFallback();
      }
    },
    [workspaceId, openOpportunityItem, openOpportunitiesFallback],
  );

  // The opening message names the spark, and the spark also travels as the turn's metadata, which
  // both runtimes render into the persona's prompt. With neither, the persona greeted the user
  // generically, knowing nothing of the spark they had clicked on; with the metadata alone it
  // still did, because nothing in the turn asked it to take the spark up.
  const handleStartChat = useCallback(
    (spark: ParsedSpark) => {
      void startChatSession(useChatStore.getState().initializeChat, navigate, {
        agentId: SPECIAL_AGENT_IDS.AUDIENCE_FLOW,
        prompt: t('homePageDashboard.iWantToTalkAboutSpark', {
          persona: spark.persona.name,
          spark: spark.spark,
        }),
        inputs: { personaId: spark.persona.id, metadata: { personaSpark: spark } },
        visibilityScope: {
          workspaceId,
          organizationId: getCurrentOrganizationId(),
        },
      });
    },
    [navigate, workspaceId, t],
  );

  const audienceSparks = useMemo<AudienceSparksDeps>(
    () => ({
      useSparks: useAudienceSparksData,
      audienceAgentId: SPECIAL_AGENT_IDS.AUDIENCE_FLOW,
      onViewOpportunity: (opportunityId) => void handleViewOpportunity(opportunityId),
      onStartChat: handleStartChat,
    }),
    [handleViewOpportunity, handleStartChat],
  );

  const opportunitiesPreview = useMemo<OpportunitiesPreviewDeps>(
    () => ({
      workspaceId,
      lastVisitedAt,
      OpportunitiesCard: ({ item, ...rest }) => (
        <OpportunitiesCard {...rest} item={item as OpportunityCardItem} />
      ),
      onLaunchAll: openOpportunitiesFallback,
      onOpenItem: (item) => openOpportunityItem(item as OpportunityCardItem),
    }),
    [workspaceId, lastVisitedAt, openOpportunityItem, openOpportunitiesFallback],
  );

  return (
    <Homepage
      userName={userName}
      springboardTabs={springboardTabs}
      IQAnalysisPreview={IQAnalysisPreview}
      audienceSparks={audienceSparks}
      opportunitiesPreview={opportunitiesPreview}
    />
  );
};
